import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CalendarDays, Check, CheckCircle2, Download, Loader2, Video, Phone, MapPin } from "lucide-react";
import { Reveal, Eyebrow, EASE } from "@/components/Reveal";
import { SERVICE_OPTIONS, API, COMPANY } from "@/data/content";

const STEPS = ["Service", "Date & time", "Your details", "Confirm"];
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;
const MEETING_TYPES = [
  { id: "video", label: "Video call", icon: Video },
  { id: "phone", label: "Phone", icon: Phone },
  { id: "in-person", label: "Camberwell office", icon: MapPin },
];

function nextWeekdays(count) {
  const days = [];
  const d = new Date();
  while (days.length < count) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) days.push(new Date(d));
  }
  return days;
}

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const prettyDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const prettyTime = (t) => {
  const h = parseInt(t.split(":")[0], 10);
  return `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;
};

function CalendlyEmbed({ url }) {
  useEffect(() => {
    if (!document.querySelector('script[src*="assets.calendly.com"]')) {
      const s = document.createElement("script");
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.async = true;
      document.body.appendChild(s);
    } else if (window.Calendly) {
      window.Calendly.initInlineWidgets();
    }
  }, []);
  return (
    <div
      className="calendly-inline-widget rounded-3xl overflow-hidden bg-white border border-ink/8"
      data-url={`${url}?hide_gdpr_banner=1&background_color=fafafc&text_color=0f172a&primary_color=fdc830`}
      style={{ minWidth: 320, height: 720 }}
      data-testid="calendly-embed"
    />
  );
}

export default function Book() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [service, setService] = useState(params.get("service") || "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", meeting_type: "video", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const days = useMemo(() => nextWeekdays(14), []);

  useEffect(() => {
    if (!date) return;
    setSlots(null);
    setTime("");
    axios
      .get(`${API}/bookings/slots`, { params: { date } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => toast.error("Could not load availability. Please try again."));
  }, [date]);

  const serviceTitle = SERVICE_OPTIONS.find((s) => s.slug === service)?.title || "";
  const detailsValid = form.name.trim().length >= 2 && form.email.includes("@") && form.phone.trim().length >= 6;

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/bookings`, { ...form, service, date, time });
      setBooking(r.data);
      setStep(4);
    } catch (e) {
      const msg = e.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
      if (e.response?.status === 409 && date) {
        const r = await axios.get(`${API}/bookings/slots`, { params: { date } });
        setSlots(r.data.slots);
        setTime("");
        setStep(1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const downloadIcs = () => {
    if (!booking) return;
    const d = booking.date.replace(/-/g, "");
    const h = booking.time.split(":")[0];
    const ics = [
      "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Invesmo//Booking//EN", "BEGIN:VEVENT",
      `UID:${booking.reference}@invesmo.com.au`,
      `DTSTART:${d}T${h}0000`,
      `DTEND:${d}T${String(Number(h) + 1).padStart(2, "0")}0000`,
      `SUMMARY:Invesmo consultation — ${serviceTitle}`,
      `DESCRIPTION:Meeting with Anu Prakash (Invesmo). Reference ${booking.reference}. Type: ${booking.meeting_type}.`,
      "LOCATION:Camberwell VIC 3124",
      "END:VEVENT", "END:VCALENDAR",
    ].join("\r\n");
    const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `invesmo-${booking.reference}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="booking-page" className="pt-36 sm:pt-44 pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Reveal>
          <Eyebrow>Book a meeting</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-ink leading-[1.05]">
            {CALENDLY_URL ? (
              <>Half an hour with <span className="font-editorial italic font-medium text-gold-600">Anu.</span></>
            ) : (
              <>An hour with <span className="font-editorial italic font-medium text-gold-600">Anu.</span></>
            )}
          </h1>
          <p className="mt-5 text-ink/60 max-w-xl leading-relaxed">
            {CALENDLY_URL
              ? "Pick a time that suits below — it syncs straight into Anu's calendar and you'll both get instant confirmations."
              : "A free, no-obligation 60-minute consultation. Choose a discipline, pick a time that suits, and we'll confirm by email and phone."}
          </p>
        </Reveal>

        {CALENDLY_URL ? (
          <Reveal delay={0.15} className="mt-12">
            <CalendlyEmbed url={CALENDLY_URL} />
            <p className="mt-6 text-xs text-ink/40 text-center">
              Prefer to talk first? Call {COMPANY.phoneDisplay} or email {COMPANY.email}.
            </p>
          </Reveal>
        ) : (
          <>
            {step < 4 && (
              <div className="flex items-center gap-2 sm:gap-3 mt-12 mb-10" data-testid="booking-progress">
                {STEPS.map((s, i) => (
                  <div key={s} className="flex items-center gap-2 sm:gap-3 flex-1">
                    <div
                      className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500 ${
                        i < step ? "bg-brand text-white" : i === step ? "bg-obsidian text-gold" : "bg-ink/8 text-ink/40"
                      }`}
                      data-testid={`booking-step-indicator-${i}`}
                    >
                      {i < step ? <Check size={14} strokeWidth={3} /> : i + 1}
                    </div>
                    <span className={`text-xs font-semibold hidden sm:block ${i === step ? "text-ink" : "text-ink/40"}`}>{s}</span>
                    {i < STEPS.length - 1 && <div className="flex-1 h-px bg-ink/10" />}
                  </div>
                ))}
              </div>
            )}

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: EASE }}
              >
                {step === 0 && (
                  <div data-testid="booking-step-service">
                    <div className="grid sm:grid-cols-2 gap-4">
                      {SERVICE_OPTIONS.map((s) => (
                        <button
                          key={s.slug}
                          data-testid={`service-option-${s.slug}`}
                          onClick={() => setService(s.slug)}
                          className={`text-left rounded-2xl border px-6 py-5 font-medium text-sm transition-all duration-300 ${
                            service === s.slug
                              ? "border-gold bg-gold-50 text-ink shadow-gold-glow"
                              : "border-ink/10 bg-white text-ink/70 hover:border-gold/50"
                          }`}
                        >
                          {s.title}
                        </button>
                      ))}
                    </div>
                    <StepNav nextDisabled={!service} onNext={() => setStep(1)} nextTestid="booking-next-service" />
                  </div>
                )}

                {step === 1 && (
                  <div data-testid="booking-step-datetime">
                    <h3 className="font-display text-lg font-bold text-ink mb-4">Choose a day (Melbourne time)</h3>
                    <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-3 mb-10">
                      {days.map((d) => {
                        const iso = toISO(d);
                        return (
                          <button
                            key={iso}
                            data-testid={`date-option-${iso}`}
                            onClick={() => setDate(iso)}
                            className={`rounded-2xl border px-2 py-4 text-center transition-all duration-300 ${
                              date === iso ? "border-gold bg-gold-50 shadow-gold-glow" : "border-ink/10 bg-white hover:border-gold/50"
                            }`}
                          >
                            <div className="text-[10px] uppercase tracking-widest text-ink/45">{d.toLocaleDateString("en-AU", { weekday: "short" })}</div>
                            <div className="font-display text-xl font-bold text-ink mt-1">{d.getDate()}</div>
                            <div className="text-[10px] text-ink/45">{d.toLocaleDateString("en-AU", { month: "short" })}</div>
                          </button>
                        );
                      })}
                    </div>

                    {date && (
                      <div>
                        <h3 className="font-display text-lg font-bold text-ink mb-4">Available times — {prettyDate(date)}</h3>
                        {!slots ? (
                          <div className="flex items-center gap-3 text-ink/50 text-sm py-6" data-testid="slots-loading">
                            <Loader2 size={16} className="animate-spin" /> Checking availability…
                          </div>
                        ) : (
                          <div className="grid grid-cols-4 sm:grid-cols-8 gap-3" data-testid="slots-grid">
                            {slots.map((s) => (
                              <button
                                key={s.time}
                                data-testid={`slot-${s.time}`}
                                disabled={!s.available}
                                onClick={() => setTime(s.time)}
                                className={`rounded-xl border px-2 py-3 text-xs font-semibold transition-all duration-300 ${
                                  !s.available
                                    ? "border-ink/5 bg-ink/[0.03] text-ink/25 line-through cursor-not-allowed"
                                    : time === s.time
                                      ? "border-gold bg-obsidian text-gold"
                                      : "border-ink/10 bg-white text-ink/70 hover:border-gold/50"
                                }`}
                              >
                                {prettyTime(s.time)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <StepNav onBack={() => setStep(0)} nextDisabled={!date || !time} onNext={() => setStep(2)} nextTestid="booking-next-datetime" />
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="booking-step-details">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Full name" testid="booking-name-field">
                        <input data-testid="booking-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Citizen" className={inputCls} />
                      </Field>
                      <Field label="Email" testid="booking-email-field">
                        <input data-testid="booking-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" className={inputCls} />
                      </Field>
                      <Field label="Phone" testid="booking-phone-field">
                        <input data-testid="booking-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="04XX XXX XXX" className={inputCls} />
                      </Field>
                      <Field label="Meeting type" testid="booking-meeting-type-field">
                        <div className="grid grid-cols-3 gap-2">
                          {MEETING_TYPES.map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              data-testid={`meeting-type-${m.id}`}
                              onClick={() => setForm({ ...form, meeting_type: m.id })}
                              className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-[11px] font-semibold transition-all duration-300 ${
                                form.meeting_type === m.id ? "border-gold bg-gold-50 text-ink" : "border-ink/10 text-ink/50 hover:border-gold/50"
                              }`}
                            >
                              <m.icon size={16} />
                              {m.label}
                            </button>
                          ))}
                        </div>
                      </Field>
                    </div>
                    <Field label="Anything we should know? (optional)" testid="booking-notes-field" className="mt-5">
                      <textarea data-testid="booking-notes-input" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Briefly describe your scenario…" className={`${inputCls} resize-none`} />
                    </Field>
                    <p className="text-[11px] text-ink/40 mt-4 leading-relaxed">
                      Your details are handled under our <Link to="/privacy" className="underline hover:text-gold-700">Privacy Policy</Link> and
                      used only to arrange your consultation.
                    </p>
                    <StepNav onBack={() => setStep(1)} nextDisabled={!detailsValid} onNext={() => setStep(3)} nextTestid="booking-next-details" />
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="booking-step-confirm">
                    <div className="rounded-3xl bg-obsidian text-white p-8 sm:p-10 grain relative overflow-hidden">
                      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
                      <div className="flex items-center gap-3 text-gold text-xs font-semibold uppercase tracking-[0.25em] mb-6">
                        <CalendarDays size={15} /> Your consultation
                      </div>
                      <div className="font-display text-2xl sm:text-3xl font-bold">{serviceTitle}</div>
                      <div className="mt-2 text-white/60">{prettyDate(date)} · {prettyTime(time)} · {MEETING_TYPES.find((m) => m.id === form.meeting_type)?.label}</div>
                      <div className="mt-8 pt-8 border-t border-white/10 grid sm:grid-cols-3 gap-6 text-sm">
                        <div><div className="text-[11px] uppercase tracking-widest text-white/45">Name</div><div className="mt-1">{form.name}</div></div>
                        <div><div className="text-[11px] uppercase tracking-widest text-white/45">Email</div><div className="mt-1">{form.email}</div></div>
                        <div><div className="text-[11px] uppercase tracking-widest text-white/45">Phone</div><div className="mt-1">{form.phone}</div></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-8">
                      <button data-testid="booking-back-confirm" onClick={() => setStep(2)} className="inline-flex items-center gap-2 text-sm font-semibold text-ink/50 hover:text-ink transition-colors">
                        <ArrowLeft size={15} /> Back
                      </button>
                      <button
                        data-testid="booking-submit"
                        onClick={submit}
                        disabled={submitting}
                        className="group inline-flex items-center gap-3 rounded-full bg-gold text-obsidian px-8 py-4 text-sm font-bold tracking-wide transition-all duration-300 hover:shadow-gold-glow disabled:opacity-60"
                      >
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                        Confirm booking
                        {!submitting && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
                      </button>
                    </div>
                  </div>
                )}

                {step === 4 && booking && (
                  <div data-testid="booking-success" className="text-center py-8">
                    <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6, ease: EASE }} className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-light mb-8">
                      <CheckCircle2 size={38} className="text-brand" strokeWidth={1.6} />
                    </motion.div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold text-ink">You're booked in.</h2>
                    <p className="mt-4 text-ink/60 max-w-md mx-auto leading-relaxed">
                      {serviceTitle} — {prettyDate(booking.date)} at {prettyTime(booking.time)}. A confirmation email is
                      on its way to you, and Anu has been notified.
                    </p>
                    <div className="mt-6 inline-block rounded-full bg-obsidian text-gold text-sm font-bold tracking-[0.2em] px-6 py-3" data-testid="booking-reference">
                      {booking.reference}
                    </div>
                    <div className="mt-10 flex flex-wrap justify-center gap-4">
                      <button data-testid="booking-ics-download" onClick={downloadIcs} className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-7 py-3.5 text-sm font-semibold text-ink hover:border-gold hover:text-gold-700 transition-all duration-300">
                        <Download size={15} /> Add to calendar
                      </button>
                      <Link to="/" data-testid="booking-home-link" className="rounded-full bg-obsidian text-gold px-7 py-3.5 text-sm font-semibold transition-all duration-300 hover:bg-gold hover:text-obsidian">
                        Back to home
                      </Link>
                    </div>
                    <p className="mt-8 text-xs text-ink/40">
                      Need to change something? Call {COMPANY.phoneDisplay} or email {COMPANY.email} quoting your reference.
                    </p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-ink/10 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-ink/30 focus:outline-none focus:ring-2 focus:ring-gold/60 focus:border-gold transition-all duration-300";

function Field({ label, children, className = "", testid }) {
  return (
    <div className={className} data-testid={`${testid}-wrap`}>
      <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-ink/50 mb-2.5">{label}</label>
      {children}
    </div>
  );
}

function StepNav({ onBack, onNext, nextDisabled, nextTestid }) {
  return (
    <div className="flex items-center justify-between mt-10">
      {onBack ? (
        <button data-testid="booking-back" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-ink/50 hover:text-ink transition-colors">
          <ArrowLeft size={15} /> Back
        </button>
      ) : (
        <span />
      )}
      <button
        data-testid={nextTestid}
        onClick={onNext}
        disabled={nextDisabled}
        className="group inline-flex items-center gap-3 rounded-full bg-obsidian text-gold px-8 py-4 text-sm font-semibold tracking-wide transition-all duration-300 hover:bg-gold hover:text-obsidian hover:shadow-gold-glow disabled:opacity-40 disabled:pointer-events-none"
      >
        Continue
        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </div>
  );
}
